// sqlite-store.js — the ON-DEVICE storage backends for the Capacitor port.
//
// The app has two web storage seams (outbox-store.js → IndexedDB, medplum-live.js
// → localStorage). On a real device (Capacitor Android/iOS WebView) both must
// live in native, ENCRYPTED SQLite (SQLCipher, via @capacitor-community/sqlite)
// so that queued clinical writes and cached patient data never sit in the
// WebView's plaintext IndexedDB/localStorage. This module provides the two
// SQLite-backed implementations; the seams themselves choose between web and
// device at their existing construction points (getOutbox in outbox.js,
// the swrStorage seam in medplum-live.js) via capacitor-env.isCapacitorNative().
//
// Two backends, one file (they share the encrypted-DB open helper):
//   1. createSqliteOutboxStore()        — satisfies the exact OutboxStore
//                                          contract (put/get/allOrdered/patch/
//                                          remove/byStatus/nextSeq), byte-for-
//                                          byte the same semantics as the
//                                          IndexedDB IdbOutboxStore.
//   2. createSqliteReadCacheStorage()   — a localStorage-SHAPED synchronous KV
//                                          (getItem/setItem/removeItem) whose
//                                          durable backing is the encrypted DB.
//                                          It keeps a warm in-memory mirror so
//                                          medplum-live.js's SYNCHRONOUS
//                                          readCache/writeCache never had to
//                                          change signature (see the design note
//                                          on that module's swrStorage seam).
//
// ── Encryption / key-at-rest (the PHI requirement) ──
// We use the plugin's SQLCipher "encryption" support. The flow:
//   * isInConfigEncryption() — is DB encryption enabled in capacitor.config?
//     On native it MUST be (the native config + Keystore live in android/, owned
//     by the config/native phase). If it is NOT, we THROW rather than silently
//     open a plaintext DB and leak PHI (AGENTS §1.3 — fail loud, no unsafe
//     fallback).
//   * isSecretStored() → setEncryptionSecret(secret) on first launch only. The
//     secret is a fresh 256-bit random value generated on-device; we hand it to
//     the plugin ONCE and never keep or re-supply it. What protects it at rest:
//     on Android the plugin persists the secret in EncryptedSharedPreferences
//     whose master key is held in the AndroidKeyStore (hardware-backed on
//     supported devices), so the SQLCipher passphrase is never in the JS bundle,
//     never in plaintext prefs, and never recoverable off-device. We always open
//     with mode 'secret' (plugin supplies the stored key): SQLCipher's
//     openOrCreateDatabase(file, passphrase) CREATES a brand-new DB encrypted
//     (ciphertext from the very first byte) and opens an existing one, so 'secret'
//     is correct for both. Mode 'encryption' is ONLY for migrating an existing
//     PLAINTEXT DB to encrypted — our DBs are born encrypted, so it would make the
//     native open fail ("Failed in encryption … not found", no plaintext file to
//     encrypt). Proven on the Phase-5 emulator; the injected test fake can't catch
//     this because it doesn't model SQLCipher.
//
// ── Testability ──
// Every entry point accepts an INJECTED `sqlite` connection object (the
// SQLiteConnection API surface). The contract tests inject a faithful fake of
// that API backed by a REAL SQLite engine (node:sqlite), so the tests exercise
// the actual SQL this module issues — real ORDER BY / WHERE / INSERT OR REPLACE
// — not a hand-mocked approximation. When no `sqlite` is injected (the real app
// path) we dynamic-import the plugin, so the plugin is never loaded in the web
// bundle or under jsdom. NOTE: the fake proves the SQL semantics; the real
// native SQLCipher encryption + Keystore behaviour is proven on the emulator in
// Phase 5, not by these tests.

// ────────────────────────────────────────────────────────────────────────────
// Plugin loading + encrypted-DB open
// ────────────────────────────────────────────────────────────────────────────

const OUTBOX_DB = 'healther-outbox';
const READCACHE_DB = 'healther-readcache';
const DB_VERSION = 1;

// ── The ONE shared SQLiteConnection (BUG #1) ─────────────────────────────────
// Both backends (outbox + read cache) MUST share a single SQLiteConnection
// instance. The plugin keeps a JS-side connection registry (`_connectionDict`)
// PER SQLiteConnection object; `checkConnectionsConsistency()` reconciles that
// JS registry against the NATIVE one by CLOSING every native connection the JS
// side doesn't list (native `closeAllConnections()` when the JS list is empty —
// see the plugin's `CapacitorSQLite.java checkConnectionsConsistency`). If each
// backend built its OWN SQLiteConnection, the second one's consistency check —
// seeing only its own db in its fresh JS dict — would close the FIRST backend's
// live connection. One shared instance (and reconcile-once, below) is the only
// safe shape. Memoized so the whole module hands out the same object.
let _sharedConnPromise = null;
async function loadNativeSqlite() {
  if (!_sharedConnPromise) {
    _sharedConnPromise = (async () => {
      const mod = await import('@capacitor-community/sqlite');
      return new mod.SQLiteConnection(mod.CapacitorSQLite);
    })();
  }
  return _sharedConnPromise;
}

// A WebView full-document navigation (every login / screen boot in this app)
// re-runs the JS bundle — so `_connectionDict` is born empty — while the NATIVE
// connection registry SURVIVES the reload. A plain `createConnection()` then
// fails "Connection <db> already exists" and the store used to silently degrade
// to in-memory (0 rows persisted → offline "Failed to fetch"). The plugin's
// documented reload recovery is `checkConnectionsConsistency()`: with an empty
// JS dict it makes the native side `closeAllConnections()`, giving us a clean
// slate to `createConnection()` into. We run it exactly ONCE per shared
// connection (memoized promise) BEFORE any create — a second run after the
// first backend has created its connection would (correctly) see that
// connection as consistent, but running it once keeps the reconcile atomic and
// avoids any window where a racing check could close a just-opened connection.
const _reconcileByConn = new WeakMap(); // SQLiteConnection → Promise (run-once)
function reconcileConnections(conn) {
  let p = _reconcileByConn.get(conn);
  if (!p) {
    p = (async () => {
      // A false result / rejection means the plugin had to CLEAR a stale native
      // registry — that is the recovery we want, not an error. Either way the
      // native side is now reconciled and createConnection can proceed.
      try { await conn.checkConnectionsConsistency(); } catch (_) { /* reconciled-by-clearing */ }
    })();
    _reconcileByConn.set(conn, p);
  }
  return p;
}

/** A fresh 256-bit passphrase, base64. Handed to the plugin once, never kept. */
function randomSecret() {
  const c = (typeof globalThis !== 'undefined' && globalThis.crypto) || null;
  if (!c || !c.getRandomValues) throw new Error('sqlite-store: secure RNG unavailable — cannot provision DB encryption secret');
  const bytes = new Uint8Array(32);
  c.getRandomValues(bytes);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return (typeof btoa === 'function') ? btoa(bin) : Buffer.from(bytes).toString('base64');
}

/**
 * Open (creating on first launch) an ENCRYPTED SQLite DB and return the opened
 * connection. Encapsulates the whole SQLCipher secret dance so both backends
 * share exactly one code path.
 */
async function openEncryptedDb(sqlite, dbName, version) {
  // (1) Encryption MUST be configured on native. If it isn't, fail loud — never
  // open a plaintext DB that would hold PHI.
  const configEnc = await sqlite.isInConfigEncryption();
  if (!configEnc || !configEnc.result) {
    throw new Error(
      'sqlite-store: database encryption is not enabled in capacitor.config — refusing to store PHI unencrypted. '
      + 'Enable SQLCipher encryption in the native config before shipping.',
    );
  }

  // (2) First launch → provision a random secret into the Keystore-backed store.
  const stored = await sqlite.isSecretStored();
  if (!stored || !stored.result) {
    await sqlite.setEncryptionSecret(randomSecret());
  }

  // (3) Reconcile the reload-surviving native connection registry ONCE (BUG #1),
  // then reuse a surviving connection or open a fresh one.
  await reconcileConnections(sqlite);

  // (4) If this db already has a live JS-side connection (same load, e.g. a
  // defensive re-open), REUSE it instead of createConnection() throwing
  // "Connection already exists". After the reconcile above, a reloaded launch
  // has an empty JS dict → isConnection is false → we create fresh.
  const RO = false;
  let db;
  const existing = await sqlite.isConnection(dbName, RO);
  if (existing && existing.result) {
    db = await sqlite.retrieveConnection(dbName, RO);
  } else {
    // Open with the stored passphrase (mode 'secret'). SQLCipher's
    // openOrCreateDatabase(file, passphrase) creates the DB encrypted when it
    // does not yet exist and opens it when it does, so 'secret' is correct for
    // both the first launch and every launch after. (Mode 'encryption' is only
    // for migrating an existing plaintext DB — see the header note; using it on
    // a born-encrypted DB fails the native open with "Failed in encryption …
    // not found".)
    db = await sqlite.createConnection(dbName, /* encrypted */ true, 'secret', version, RO);
  }

  // (5) Open if not already open (a retrieved connection may still be open).
  let opened = null;
  try { opened = await db.isDBOpen(); } catch (_) { opened = null; }
  if (!opened || !opened.result) await db.open();
  return db;
}

// query() returns { values: [...] }; normalise to a plain array of row objects.
function rowsOf(res) {
  return (res && Array.isArray(res.values)) ? res.values : [];
}

// ────────────────────────────────────────────────────────────────────────────
// 1. Outbox store — SQLite implementation of the OutboxStore contract
// ────────────────────────────────────────────────────────────────────────────
//
// One row per queued write. The full durable entry (see outbox-ops.js) is kept
// verbatim as JSON in `payload`; the columns id/seq/status/localRef are
// projected out of it purely so the DB can index + filter/order the same way
// the IndexedDB store's keyPath + indexes did:
//   id  PRIMARY KEY   — put/get/remove by id (IDB keyPath 'id')
//   seq INTEGER       — allOrdered() = ORDER BY seq ASC (IDB index 'seq')
//   status TEXT       — byStatus() = WHERE status = ? (IDB index 'status')
//   localRef TEXT     — parity with IDB index 'localRef' (unused by the engine
//                       today but kept so the shape matches 1:1)
// The `meta` table holds the monotonic seq counter, exactly like the IDB META
// store's { k:'seq', v:<number> } row.

const OUTBOX_DDL = [
  `CREATE TABLE IF NOT EXISTS outbox (
     id TEXT PRIMARY KEY,
     seq INTEGER,
     status TEXT,
     localRef TEXT,
     payload TEXT NOT NULL
   );`,
  `CREATE INDEX IF NOT EXISTS idx_outbox_seq ON outbox(seq);`,
  `CREATE INDEX IF NOT EXISTS idx_outbox_status ON outbox(status);`,
  `CREATE INDEX IF NOT EXISTS idx_outbox_localRef ON outbox(localRef);`,
  `CREATE TABLE IF NOT EXISTS meta (k TEXT PRIMARY KEY, v INTEGER);`,
].join('\n');

class SqliteOutboxStore {
  constructor(db) {
    this.db = db;
    // Serialize nextSeq() so concurrent enqueues can't read the same base value
    // (see nextSeq for why a DB transaction can't do this on this plugin).
    this._seqTail = Promise.resolve();
  }

  async _init() {
    await this.db.execute(OUTBOX_DDL);
    return this;
  }

  /** Insert (or overwrite) a full entry row. Returns the entry (like IDB put). */
  async put(entry) {
    await this.db.run(
      'INSERT OR REPLACE INTO outbox (id, seq, status, localRef, payload) VALUES (?, ?, ?, ?, ?);',
      [entry.id, entry.seq, entry.status, entry.localRef == null ? null : entry.localRef, JSON.stringify(entry)],
    );
    return entry;
  }

  /** Fetch one entry by id, or undefined. */
  async get(id) {
    const res = await this.db.query('SELECT payload FROM outbox WHERE id = ?;', [id]);
    const rows = rowsOf(res);
    return rows.length ? JSON.parse(rows[0].payload) : undefined;
  }

  /** All entries in ascending `seq` (the FIFO replay order). */
  async allOrdered() {
    const res = await this.db.query('SELECT payload FROM outbox ORDER BY seq ASC;', []);
    return rowsOf(res).map((r) => JSON.parse(r.payload));
  }

  /** Shallow-merge `changes` onto the stored entry `id`; returns the merged row. */
  async patch(id, changes) {
    const cur = await this.get(id);
    if (!cur) throw new Error('outbox.patch: no entry ' + id);
    const next = { ...cur, ...changes };
    await this.put(next);
    return next;
  }

  /** Delete one entry by id. */
  async remove(id) {
    await this.db.run('DELETE FROM outbox WHERE id = ?;', [id]);
  }

  /** All entries with the given status (unordered — callers sort by seq if needed). */
  async byStatus(status) {
    const res = await this.db.query('SELECT payload FROM outbox WHERE status = ?;', [status]);
    return rowsOf(res).map((r) => JSON.parse(r.payload));
  }

  /**
   * Increment and return the next monotonic seq, so two enqueues can never draw
   * the same value.
   *
   * We do NOT wrap this read-modify-write in a SQL transaction. On
   * @capacitor-community/sqlite (v8) none of the plugin's transaction primitives
   * fit here — verified against its source, not assumed (AGENTS §1.5):
   *   • `execute('BEGIN'/'COMMIT')` — the ORIGINAL code, and the device bug:
   *     `execute(stmts, transaction=true)` (the default) itself calls the native
   *     `beginTransaction()` before and `commitTransaction()` after the batch
   *     (Database.java execute()), so a hand-rolled `COMMIT`/`ROLLBACK` string
   *     runs with no matching open transaction → "Cannot perform this operation
   *     because there is no current transaction" and the INSERT is LOST.
   *   • `INSERT … ON CONFLICT DO UPDATE … RETURNING v` — SQLCipher 4.10.0 is
   *     SQLite ≥ 3.35 so RETURNING parses, but the plugin re-derives INSERT
   *     RETURNING rows by rowid range (`getInsertReturnedValues` SELECTs
   *     `WHERE rowid BETWEEN initLastId+1 AND lastId`); an UPSERT that takes the
   *     UPDATE branch creates no new rowid, so it returns EMPTY on every call
   *     after the first — unusable for reading the bumped value.
   *   • connection-level `beginTransaction()` — throws "Already in transaction"
   *     if a second nextSeq races in on the same (shared, single) connection, so
   *     it can't guard concurrent enqueues either.
   * The correct, plugin-safe atomicity boundary is JS-layer serialization: chain
   * each read-modify-write on a promise tail so they run strictly one at a time.
   * The bump itself is a single-statement `INSERT OR REPLACE` (the plugin auto-
   * commits that one statement), which is durable on its own; serialization only
   * prevents two concurrent reads seeing the same base.
   */
  nextSeq() {
    const step = async () => {
      const res = await this.db.query("SELECT v FROM meta WHERE k = 'seq';", []);
      const rows = rowsOf(res);
      const next = ((rows.length && rows[0].v) || 0) + 1;
      await this.db.run("INSERT OR REPLACE INTO meta (k, v) VALUES ('seq', ?);", [next]);
      return next;
    };
    const p = this._seqTail.then(step, step); // run after the prior bump settles (ok or error)
    this._seqTail = p.catch(() => {});         // a failed bump must not wedge the tail
    return p;
  }
}

/**
 * Open the encrypted SQLite-backed OutboxStore. Same contract as
 * createOutboxStore() in outbox-store.js. Pass `{ sqlite }` to inject a fake
 * (tests); omit it on-device to load the real plugin.
 */
export async function createSqliteOutboxStore({ sqlite, dbName = OUTBOX_DB } = {}) {
  const conn = sqlite || await loadNativeSqlite();
  const db = await openEncryptedDb(conn, dbName, DB_VERSION);
  return new SqliteOutboxStore(db)._init();
}

// ────────────────────────────────────────────────────────────────────────────
// 2. Read cache — a localStorage-shaped sync KV over the encrypted DB
// ────────────────────────────────────────────────────────────────────────────
//
// medplum-live.js's readCache/writeCache are SYNCHRONOUS and call
// storage.getItem/setItem directly. To keep that module's SWR hot path
// unchanged (see its design note), the device backend must present a SYNCHRONOUS
// getItem/setItem/removeItem. It does so via a warm in-memory mirror (a Map):
//   * getItem(k)      → read the mirror synchronously. Before the DB has warmed,
//                       an untouched key returns null → a plain SWR cache MISS →
//                       the loader does a fresh authorized read (exactly what a
//                       first-ever web load does). Safe.
//   * setItem(k, v)   → update the mirror synchronously AND schedule an async
//                       persist to the encrypted DB. Same-session reads are
//                       correct immediately; the DB catches up in the background.
//   * removeItem(k)   → mirror delete + async DB delete.
// The mirror is process memory (fine for PHI); the DURABLE copy is the encrypted
// DB — localStorage is never touched on device.
//
// Warm/merge order avoids clobbering session-fresh writes: on open we load every
// persisted row into the mirror EXCEPT keys already written this session (those
// stay the session value and are flushed to the DB). Pending writes captured
// before the DB opened are flushed once it does.

const READCACHE_DDL =
  'CREATE TABLE IF NOT EXISTS swr_cache (k TEXT PRIMARY KEY, v TEXT NOT NULL);';

/**
 * Construct the device read-cache storage. Returns a synchronous
 * getItem/setItem/removeItem object plus a `ready` promise that resolves once
 * the DB has opened and the mirror is warm (tests await it; the app doesn't
 * need to). Pass `{ sqlite }` to inject a fake.
 */
export function createSqliteReadCacheStorage({ sqlite, dbName = READCACHE_DB } = {}) {
  const mirror = new Map();       // k → raw JSON string (the exact localStorage value)
  const touchedThisSession = new Set(); // keys written before/after warm — session wins
  let db = null;
  let warmed = false;

  // Async ops are chained onto this tail so writes/deletes to the DB stay
  // ordered relative to each other and to the warm load.
  let tail = (async () => {
    const conn = sqlite || await loadNativeSqlite();
    db = await openEncryptedDb(conn, dbName, DB_VERSION);
    await db.execute(READCACHE_DDL);
    const res = await db.query('SELECT k, v FROM swr_cache;', []);
    for (const row of rowsOf(res)) {
      if (!touchedThisSession.has(row.k)) mirror.set(row.k, row.v);
    }
    warmed = true;
    // Flush anything written before the DB was ready.
    for (const k of touchedThisSession) {
      const v = mirror.get(k);
      if (v === undefined) {
        await db.run('DELETE FROM swr_cache WHERE k = ?;', [k]);
      } else {
        await db.run('INSERT OR REPLACE INTO swr_cache (k, v) VALUES (?, ?);', [k, v]);
      }
    }
  })();
  // A warm/open failure must not wedge the tail as a rejected promise (which
  // would fail every later persist). Surface it LOUDLY, ONCE (this is a genuine
  // failure now that the reload "Connection already exists" degrade is gone —
  // see openEncryptedDb BUG #1), then behave as a cache MISS: the mirror stays
  // empty for un-written keys, so getItem() returns null → SWR does a fresh
  // authorized read, exactly the web semantics for a storage failure. A read
  // cache losing durability is "slower next launch", never a lost clinical write
  // (those live in the OUTBOX, whose store THROWS on open failure — it must
  // never silently drop a queued write).
  tail = tail.catch((e) => {
    // eslint-disable-next-line no-console
    console.error(
      '[sqlite-readcache] encrypted DB open/warm FAILED — offline read cache degraded to cache-miss '
      + '(SWR falls back to always-fresh; durable offline reads unavailable this launch):',
      e,
    );
  });
  // The initial open+warm, settled exactly once (the catch above means it never
  // rejects). whenWarm() hands this to swrLoad so a cold read AWAITS the mirror
  // being populated before deciding MISS — without it, a cold OFFLINE boot reads
  // the still-empty mirror, misses, runs the loader, and throws "Failed to fetch"
  // even though the durable rows are milliseconds from loading. Distinct from
  // ready(), which tracks the ever-moving persist tail.
  const warmPromise = tail;

  const persist = (k) => {
    tail = tail.then(async () => {
      if (!db) return; // open failed (logged loudly above) → cache-miss, no durable copy
      const v = mirror.get(k);
      if (v === undefined) {
        await db.run('DELETE FROM swr_cache WHERE k = ?;', [k]);
      } else {
        await db.run('INSERT OR REPLACE INTO swr_cache (k, v) VALUES (?, ?);', [k, v]);
      }
    }).catch((e) => {
      // eslint-disable-next-line no-console
      console.error('[sqlite-readcache] persist FAILED for', k, '—', e && e.message);
    });
  };

  return {
    /** True once the DB is open and the mirror warmed (before this, reads may miss). */
    isWarm() { return warmed; },
    /** Resolves when open+warm (or the degraded-catch) settles. Tests await this. */
    ready() { return tail; },
    /** Resolves ONCE the initial open+warm settles (never rejects). swrLoad awaits
     *  this so a cold read consults the durable cache instead of racing to a miss. */
    whenWarm() { return warmPromise; },

    getItem(k) {
      const v = mirror.get(k);
      return v === undefined ? null : v;
    },
    setItem(k, v) {
      mirror.set(k, String(v));
      touchedThisSession.add(k);
      persist(k);
    },
    removeItem(k) {
      mirror.delete(k);
      touchedThisSession.add(k);
      persist(k);
    },
  };
}
